import { UserOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { setBillGuests, type BillOut } from "../../../../../api/bills";
import { useConfiguration } from "../../../../../hooks/useConfiguration";
import { useOrderingMessage } from "../../useOrderingMessage";
import type { SecondaryAction } from "../OptionsPopover";
import { useGuestCountPrompt } from "./useGuestCountPrompt";

interface TableGuestsParams {
  selectedBill: BillOut | undefined;
  canOpenBill: boolean;
  canCloseBill: boolean;
  /** Закрита без бон или в процес на закриване — действията по нея мълчат. */
  isActionDisabled: boolean;
  /** Подсказката „Недостъпно без интернет" в локален режим; иначе undefined. */
  offlineLock: string | undefined;
}

/**
 * Броят гости в лентата под масите (Конфигурация → Маркиране → „Въвеждане
 * на брой гости"). Изваден от TableFooter:
 *
 * - `askBeforeClose(bill)` — при „При закриване": модалът излиза преди
 *   „Закрий" САМО ако подадената отворена сметка още няма брой (`guests` от
 *   `GET /bills`); `false` = „Отказ" и „Закрий" не се отваря;
 * - `guestsAction` — бутонът „Брой гости" в „Опции" при включена настройка
 *   (и двата момента): същият модал, тръгва от въведения брой и го
 *   презаписва (`PATCH /bills/{id}/guests`). В локален режим е заключен.
 *
 * След всеки запис списъкът със сметки се опреснява — иконката на картата и
 * проверката „вече въведен" четат `guests` от него.
 */
export const useTableGuests = ({
  selectedBill,
  canOpenBill,
  canCloseBill,
  isActionDisabled,
  offlineLock,
}: TableGuestsParams) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const queryClient = useQueryClient();
  const { configuration } = useConfiguration();
  const { askFor, askGuestCount, guestCountModal, guestCountModalOpen } =
    useGuestCountPrompt();

  const openBill = selectedBill?.status === "open" ? selectedBill : null;

  const saveGuests = async (billId: number, guests: number) => {
    await setBillGuests(billId, guests);
    void queryClient.invalidateQueries({ queryKey: ["bills"] });
  };

  // Сметката идва изрично (фиксирана при натискането на „Закрий" —
  // payment/useCloseBillTarget), не от избора: той може да се смени, докато
  // въпросът е на екрана.
  const askBeforeClose = async (bill: BillOut): Promise<boolean> => {
    if (!askFor("close") || bill.status !== "open" || bill.guests != null) {
      return true;
    }
    const billId = bill.id;
    const guests = await askGuestCount({
      save: (count) => saveGuests(billId, count),
    });
    return guests != null;
  };

  const guestsAction: SecondaryAction | null =
    configuration.orderingGuestCount !== "off" && (canOpenBill || canCloseBill)
      ? {
          key: "guests",
          lockedTooltip: offlineLock,
          label: t("ordering.guestCount.title"),
          icon: <UserOutlined />,
          disabled: !openBill || isActionDisabled,
          onClick: () => {
            if (!openBill) return;
            const billId = openBill.id;
            void askGuestCount({
              initialCount: openBill.guests,
              save: async (count) => {
                await saveGuests(billId, count);
                message.success(t("ordering.guestCount.saved"));
              },
            });
          },
        }
      : null;

  return { askBeforeClose, guestsAction, guestCountModal, guestCountModalOpen };
};
